import { toast } from 'react-hot-toast'
import { FeatureName } from '@wingwork/common/src/constants/features'
import { useEffect } from 'react'
import useQuery from 'src/hooks/useQuery'
import { isWingworkUser } from 'src/utils/permissions'
import { getCookie, getQueryParam, setCookie, removeCookie } from 'src/utils/cookies'

export const GET_FEATURE_SETTING = gql`
  query GetFeatureSetting($input: TenantFeatureQueryInput) {
    tenantFeatures(input: $input) {
      featureName
      enabled
      orgSlug
    }
  }
`

// Global cache of enabled features per org
const enabledFeaturesCache: Record<string, Set<FeatureName>> = {}
// Track if we've fetched for an org
const hasFetched: Record<string, boolean> = {}
// Track loading state per org
const loadingOrgs: Record<string, boolean> = {}

// Utility function to directly check if a feature is enabled for an org
export const hasFeature = (
  featureName: FeatureName,
  orgSlug: string
): boolean => {
  if (!orgSlug) return false

  // Check if user is a Wingwork user and has enableAllFeature set
  if (isWingworkUser()) {
    const enableAllFeature = getQueryParam('enableAllFeature') === '1' || getCookie('enableAllFeature') === '1'
    if (enableAllFeature) {
      // If query param is set, set the cookie
      if (getQueryParam('enableAllFeature') === '1') {
        setCookie('enableAllFeature', '1')
      }
      return true
    }
    if (getQueryParam('enableAllFeature') === '0') {
      removeCookie('enableAllFeature')
    }
  }

  return enabledFeaturesCache[orgSlug]?.has(featureName) ?? false
}

const useHasFeature = (featureName: FeatureName, orgSlug: string) => {
  const { loading, hasLoaded } = useQuery(GET_FEATURE_SETTING, {
    variables: { input: { orgSlug } },
    skip: !orgSlug || hasFetched[orgSlug] || loadingOrgs[orgSlug],
    onCompleted: (data) => {
      const enabledFeatures = new Set<FeatureName>(
        (data.tenantFeatures ?? [])
          .filter((feature) => feature.enabled)
          .map((feature) => feature.featureName)
      )
      enabledFeaturesCache[orgSlug] = enabledFeatures
      hasFetched[orgSlug] = true
      loadingOrgs[orgSlug] = false
    },
    onError: (error) => {
      console.error(error)
      toast.error(
        'Something went wrong determining your feature settings. Please contact support.'
      )
      loadingOrgs[orgSlug] = false
      hasFetched[orgSlug] = true
    },
  })
  const isInternalUser = isWingworkUser()
  const cookieValue = React.useMemo(() => getCookie('enableAllFeature'), [])
  const queryParamValue = React.useMemo(() => getQueryParam('enableAllFeature'), [])

  // Set loading state when query starts
  // it prevents race conditions when the query is called multiple times for the same org
  useEffect(() => {
    if (loading && orgSlug) {
      loadingOrgs[orgSlug] = true
    }
    return () => {
      if (loading && orgSlug && loadingOrgs[orgSlug]) {
        loadingOrgs[orgSlug] = false
      }
    }
  }, [loading, orgSlug])

  // Check if user is a Wingwork user and has enableAllFeature set
  const isWingworkUserWithAllFeatures = React.useMemo(() => isInternalUser &&
    (queryParamValue === '1' || cookieValue === '1'), [isInternalUser, queryParamValue, cookieValue])

  // Set cookie if query param is present
  useEffect(() => {
    if (isInternalUser) {
      if (queryParamValue === '1') {
        setCookie('enableAllFeature', '1')
      } else if (queryParamValue === '0') {
        removeCookie('enableAllFeature')
      }
    }
  }, [isInternalUser, queryParamValue])

  return {
    hasFeature: isWingworkUserWithAllFeatures || (enabledFeaturesCache[orgSlug]?.has(featureName) ?? false),
    loading: loading || loadingOrgs[orgSlug],
    hasLoaded: hasLoaded || hasFetched[orgSlug],
  }
}

export default useHasFeature
